import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { getTeacherAuth, teacherApi } from "./teacherApi";

const menu = [
  { label: "Dashboard", path: "/teach-with-us/dashboard", icon: "🏠" },
  { label: "My Schedule", path: "/teach-with-us/schedule", icon: "📅" },
  { label: "My Batches", path: "/teach-with-us/batches", icon: "👥" },
  { label: "Study Material", path: "/teach-with-us/materials", icon: "📚" },
  { label: "Test Engine", path: "/teach-with-us/tests", icon: "📝" },
  { label: "Question Bank", path: "/teach-with-us/questions", icon: "❓" },
  { label: "Doubt Portal", path: "/teach-with-us/doubts", icon: "💬" },
  { label: "Students & Reports", path: "/teach-with-us/students", icon: "📈" },
  { label: "Settings", path: "/teach-with-us/settings", icon: "⚙️" },
];

const TeacherLayout: React.FC = () => {
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const auth = getTeacherAuth();
    if (!auth?.token) {
      navigate("/login", { replace: true });
      return;
    }
    teacherApi.me().then(setTeacher).catch((err) => setError(err.message));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("mediit_admin_auth");
    navigate("/login", { replace: true });
  };

  const initials = (teacher?.name || "Teacher")
    .replace("Pro. ", "") // Title hata kar clean initials banayega
    .split(" ")
    .map((w: string) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex min-h-screen bg-[#f8fbff] text-slate-900 font-['Inter']">
      {/* 📌 PREMIUM MINIMAL SIDEBAR */}
      <aside className="fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r border-slate-200 bg-white transition-transform max-xl:-translate-x-full xl:translate-x-0">
        
        {/* LOGO AREA */}
        <div className="flex h-[76px] items-center gap-3 border-b border-slate-100 px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-sm font-black text-white shadow-md shadow-blue-600/20">
            M
          </div>
          <div>
            <h1 className="text-base font-black tracking-tight text-slate-950">Mediit Academy</h1>
          </div>
        </div>

   {/* NAVIGATION MENU (FIXED ISACTIVE ERROR) */}
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-hidden">
          <p className="mb-4 px-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Main Menu</p>
          {menu.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/teach-with-us/dashboard"}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition-all ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              {/* ✅ FIX: isActive function yahan add kiya */}
              {({ isActive }) => (
                <>
                  <span className={`text-lg ${isActive ? 'opacity-100' : 'opacity-60 grayscale'}`}>
                    {item.icon}
                  </span>
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* PROFILE BOTTOM SECTION (CLEAN FORMAT) */}
        <div className="border-t border-slate-100 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-black text-slate-700">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-black text-slate-950" title={teacher?.name}>
                {teacher?.name || "Loading..."}
              </p>
              <p className="truncate text-[11px] font-bold text-slate-500">
                {teacher?.subject || "Subject Expert"}
              </p>
            </div>
            <button 
              onClick={logout} 
              className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-xl transition" 
              title="Logout"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
            </button>
          </div>
        </div>
      </aside>

      {/* 📌 MAIN CONTENT AREA */}
      <div className="flex min-w-0 flex-1 flex-col xl:ml-[260px]">
        <header className="sticky top-0 z-40 flex h-[76px] items-center justify-between border-b border-slate-200 bg-white/80 px-6 backdrop-blur-md">
          <div className="flex items-center gap-4">
            <button className="rounded-xl bg-slate-100 p-2 text-slate-600 xl:hidden">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
            </button>
            <h2 className="text-xl font-black text-slate-900 hidden sm:block">Welcome, {teacher?.name || "Teacher"}!</h2>
          </div>
          <div className="flex items-center gap-4">
             <span className="hidden sm:inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700 ring-1 ring-emerald-200">
               {teacher?.status || "Active"}
             </span>
             <button className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition relative">
               🔔 <span className="absolute top-0 right-0 h-2.5 w-2.5 bg-red-500 rounded-full border-2 border-white"></span>
             </button>
          </div>
        </header>

        <main className="flex-1 p-6 sm:p-8 overflow-y-auto">
          {error && <div className="mb-6 rounded-2xl bg-red-50 border border-red-100 p-4 text-sm font-black text-red-600">{error}</div>}
          <Outlet context={{ teacher }} />
        </main>
      </div>
    </div>
  );
};

export default TeacherLayout;